/**
 * BUSINESS LAYER (BL): TUG OF WAR (ESTIRA Y AFLOJA)
 * Capa de lógica de negocio, validaciones defensivas, motor de precarga de imágenes en memoria RAM,
 * gestión de sesión aislada de aficionados, cálculos matemáticos per cápita e integración con el Súper Lienzo ScreenV2.
 * 
 * @module utilities/BL/TugOfWar
 */

import {
    GetThemesDL,
    GetThemeByIdDL,
    CreateThemeDL,
    UpdateThemeDL,
    DeleteThemeDL,
    CloneThemeDL,
    GetConfigDL,
    SaveConfigDL,
    GetMatchHistoryDL,
    SaveMatchResultDL,
    UploadAssetDL,
    UploadBatchMediaDL,
    ShowCoverDL,
    ShowGameSceneDL,
    StartCountdownDL,
    PauseMatchDL,
    ResumeMatchDL,
    FinishEarlyDL,
    ResetMatchDL,
    GetMatchStateDL,
    SendTapBatchDL,
    RegisterFanDL,
    UnregisterFanDL
} from '../DL/TugOfWar';

import { SetScreenV2MainDynamic } from './ScreenV2';

// =========================================================================
// MOTOR DE PRECARGA DE IMÁGENES EN MEMORIA RAM
// =========================================================================

/**
 * Resuelve y normaliza una URL de recurso multimedia para precarga o renderizado.
 * @param {string|null} rawUrl - URL absoluta, relativa o vacía.
 * @returns {string} URL formateada y sanitizada.
 */
export const ResolveMediaUrl = (rawUrl) => {
    if (!rawUrl || typeof rawUrl !== 'string') return '';
    const trimmed = rawUrl.trim();
    if (!trimmed || /^(javascript|vbscript):/i.test(trimmed)) return '';
    if (trimmed.startsWith('data:')) return trimmed;

    if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('//')) {
        return trimmed;
    }

    const apiBase = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    const hostBase = apiBase.replace(/\/api\/?$/i, '').replace(/\/+$/, '');
    const cleanPath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    return `${hostBase}${cleanPath}`;
};

export const getResolvedMediaUrl = ResolveMediaUrl;

/**
 * Descarga y precarga concurrentemente una lista de URLs de imágenes en memoria RAM del navegador.
 * Implementa deduplicación, resolución de URLs, timeout de seguridad de 8s para evitar promesas colgadas
 * y limpieza estricta de controladores de eventos para prevenir fugas de memoria.
 * 
 * @param {Array<string>} [urls=[]] - Colección de URLs de imágenes a precargar.
 * @returns {Promise<{total: number, loaded: number, failed: number, results: Array<{url: string, success: boolean}>}>} Reporte de precarga.
 */
export const PreloadImages = async (urls = []) => {
    if (!urls || !Array.isArray(urls)) {
        return { total: 0, loaded: 0, failed: 0, results: [] };
    }

    // Deduplicar y normalizar URLs válidas omitiendo cadenas vacías y Data URIs
    const validUrls = Array.from(new Set(
        urls
            .filter((url) => typeof url === 'string' && url.trim().length > 0)
            .map((url) => ResolveMediaUrl(url))
            .filter((url) => url && !url.startsWith('data:'))
    ));

    if (validUrls.length === 0) {
        return { total: 0, loaded: 0, failed: 0, results: [] };
    }

    const preloadPromises = validUrls.map((imageUrl) => {
        return new Promise((resolve) => {
            let settled = false;
            const img = new Image();

            const onSettled = (success) => {
                if (settled) return;
                settled = true;
                if (safetyTimeoutId) clearTimeout(safetyTimeoutId);
                img.onload = null;
                img.onerror = null;
                img.onabort = null;
                resolve({ url: imageUrl, success: Boolean(success) });
            };

            // Timeout de seguridad de 8 segundos para redes lentas o recursos caídos
            const safetyTimeoutId = setTimeout(() => {
                onSettled(false);
            }, 8000);

            img.onload = () => onSettled(true);
            img.onerror = () => onSettled(false);
            img.onabort = () => onSettled(false);
            img.src = imageUrl;
        });
    });

    const settledResults = await Promise.allSettled(preloadPromises);
    const results = settledResults.map((item, idx) => {
        if (item.status === 'fulfilled') {
            return item.value;
        }
        return { url: validUrls[idx], success: false };
    });

    const loadedCount = results.filter((r) => r.success).length;
    const failedCount = results.length - loadedCount;

    return {
        total: results.length,
        loaded: loadedCount,
        failed: failedCount,
        results
    };
};

/**
 * Extrae y precarga en memoria RAM todos los recursos gráficos definidos en un tema visual de Estira y Afloja.
 * Cubre fondos de portada, juego y aficionado, logotipos de marca y patrocinador, textura de cuerda,
 * charco de lodo y los sprites de Fases 1, 2, 3, Caída 4 (Loser) y Victoria (Winner) para el Equipo 1 y el Equipo 2.
 * 
 * @param {Object|null} theme - Objeto DTO con la configuración del tema visual.
 * @returns {Promise<{total: number, loaded: number, failed: number, results: Array<{url: string, success: boolean}>}>} Reporte de precarga del tema.
 */
export const PreloadGameAssets = async (theme) => {
    if (!theme || typeof theme !== 'object') {
        return { total: 0, loaded: 0, failed: 0, results: [] };
    }

    const rawUrls = [
        // Fondos
        theme.coverBackgroundUrl || theme.CoverBackgroundUrl,
        theme.gameBackgroundUrl || theme.GameBackgroundUrl,
        theme.fanBackgroundUrl || theme.FanBackgroundUrl,
        // Logotipos
        theme.brandLogoUrl || theme.BrandLogoUrl,
        theme.sponsorLogoUrl || theme.SponsorLogoUrl,
        // Elementos interactivos
        theme.ropeTextureUrl || theme.RopeTextureUrl,
        theme.mudPuddleUrl || theme.MudPuddleUrl,
        // Sprites / Avatares del Equipo 1
        theme.player1Phase1Avatar || theme.Player1Phase1Avatar,
        theme.player1Phase2Avatar || theme.Player1Phase2Avatar,
        theme.player1Phase3Avatar || theme.Player1Phase3Avatar,
        theme.player1LoserAvatar || theme.Player1LoserAvatar || theme.player1Loser || theme.Player1Loser,
        theme.player1WinnerAvatar || theme.Player1WinnerAvatar || theme.player1Winner || theme.Player1Winner,
        // Sprites / Avatares del Equipo 2
        theme.player2Phase1Avatar || theme.Player2Phase1Avatar,
        theme.player2Phase2Avatar || theme.Player2Phase2Avatar,
        theme.player2Phase3Avatar || theme.Player2Phase3Avatar,
        theme.player2LoserAvatar || theme.Player2LoserAvatar || theme.player2Loser || theme.Player2Loser,
        theme.player2WinnerAvatar || theme.Player2WinnerAvatar || theme.player2Winner || theme.Player2Winner
    ];

    return await PreloadImages(rawUrls);
};

// =========================================================================
// GESTIÓN DE SESIÓN DEL AFICIONADO (FAN SESSION)
// =========================================================================

/**
 * Genera la clave de almacenamiento segura y aislada por sala para la sesión del aficionado móvil.
 * @param {string} [roomId=''] - Identificador GUID de la sala.
 * @returns {string} Clave de almacenamiento.
 */
const getFanSessionStorageKey = (roomId = '') => {
    const sanitizedRoomId = roomId ? String(roomId).trim() : 'default';
    return `tugofwar_fan_session_${sanitizedRoomId}`;
};

/**
 * Guarda los datos de sesión activa del aficionado móvil en sessionStorage y localStorage aislando por sala.
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {Object|string} sessionData - Datos de sesión ({ playerChoice, ... }) o string con el bando ('player1' | 'player2').
 * @returns {boolean} Verdadero si la sesión se persistió exitosamente.
 */
export const SaveFanSessionBL = (roomId, sessionData) => {
    if (!roomId) return false;

    let payload = {};
    if (typeof sessionData === 'string') {
        const choice = sessionData.toLowerCase().trim();
        if (choice !== 'player1' && choice !== 'player2') return false;
        payload = {
            roomId: String(roomId).trim(),
            playerChoice: choice,
            registeredAt: new Date().toISOString()
        };
    } else if (sessionData && typeof sessionData === 'object') {
        const choice = String(sessionData.playerChoice || sessionData.choice || '').toLowerCase().trim();
        if (choice !== 'player1' && choice !== 'player2') return false;
        payload = {
            ...sessionData,
            roomId: String(roomId).trim(),
            playerChoice: choice,
            registeredAt: sessionData.registeredAt || new Date().toISOString()
        };
    } else {
        return false;
    }

    try {
        const storageKey = getFanSessionStorageKey(roomId);
        const serializedPayload = JSON.stringify(payload);
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(storageKey, serializedPayload);
        }
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(storageKey, serializedPayload);
        }
        return true;
    } catch (storageError) {
        return false;
    }
};

/**
 * Recupera de forma segura los datos de sesión móvil del aficionado activo para una sala.
 * @param {string} [roomId=''] - Identificador GUID de la sala.
 * @returns {{roomId: string, playerChoice: string, registeredAt: string}|null} Datos de sesión o null si no existen o son inválidos.
 */
export const GetFanSessionBL = (roomId = '') => {
    try {
        const storageKey = getFanSessionStorageKey(roomId);
        let rawSessionString = null;
        if (typeof sessionStorage !== 'undefined') {
            rawSessionString = sessionStorage.getItem(storageKey);
        }
        if (!rawSessionString && typeof localStorage !== 'undefined') {
            rawSessionString = localStorage.getItem(storageKey);
        }

        if (!rawSessionString) return null;

        const parsedSession = JSON.parse(rawSessionString);
        if (
            parsedSession &&
            parsedSession.roomId &&
            (parsedSession.playerChoice === 'player1' || parsedSession.playerChoice === 'player2')
        ) {
            return {
                ...parsedSession,
                roomId: String(parsedSession.roomId),
                playerChoice: String(parsedSession.playerChoice),
                registeredAt: parsedSession.registeredAt || new Date().toISOString()
            };
        }

        return null;
    } catch (storageError) {
        return null;
    }
};

/**
 * Elimina la sesión móvil activa del aficionado para la sala especificada.
 * @param {string} [roomId=''] - Identificador GUID de la sala a limpiar.
 * @returns {boolean} Verdadero si se limpió la sesión satisfactoriamente.
 */
export const ClearFanSessionBL = (roomId = '') => {
    try {
        const storageKey = getFanSessionStorageKey(roomId);
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.removeItem(storageKey);
        }
        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem(storageKey);
        }
        return true;
    } catch (storageError) {
        return false;
    }
};

// =========================================================================
// CRUD DE TEMAS VISUALES
// =========================================================================

/**
 * Obtiene el catálogo completo de temas disponibles para Estira y Afloja con sanitización de filtros.
 * @param {string|null} [roomId=null] - Identificador GUID de la sala (opcional).
 * @param {string|null} [operatorId=null] - Identificador GUID del operador (opcional).
 * @returns {Promise<{success: boolean, data: Array<Object>, message?: string}>} Colección de temas garantizada como arreglo.
 */
export const GetThemesBL = async (roomId = null, operatorId = null) => {
    try {
        const sanitizedRoomId = roomId ? String(roomId).trim() : null;
        const sanitizedOperatorId = operatorId ? String(operatorId).trim() : null;

        const dlResponse = await GetThemesDL(sanitizedRoomId, sanitizedOperatorId);
        if (!dlResponse.success) {
            return {
                success: false,
                data: [],
                message: dlResponse.message || 'No fue posible cargar el catálogo de temas de TugOfWar.'
            };
        }

        return {
            success: true,
            data: Array.isArray(dlResponse.data) ? dlResponse.data : []
        };
    } catch (unexpectedError) {
        return {
            success: false,
            data: [],
            message: 'Error inesperado al procesar la obtención de temas de TugOfWar.'
        };
    }
};

/**
 * Obtiene el detalle de un tema de Estira y Afloja validando la presencia del identificador.
 * @param {number|string} themeIdentifier - Identificador único del tema.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Detalle del tema.
 */
export const GetThemeByIdBL = async (themeIdentifier) => {
    if (!themeIdentifier && themeIdentifier !== 0) {
        return {
            success: false,
            message: 'El identificador del tema es obligatorio.'
        };
    }

    return await GetThemeByIdDL(themeIdentifier);
};

/**
 * Sanea y crea un nuevo tema personalizado de Estira y Afloja con valores por defecto defensivos.
 * @param {Object} themeDataPayload - Datos del nuevo tema visual.
 * @param {string|null} [explicitRoomId=null] - Identificador GUID opcional de la sala.
 * @param {string|null} [explicitOperatorId=null] - Identificador GUID opcional del operador.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Tema creado y confirmado.
 */
export const CreateThemeBL = async (themeDataPayload, explicitRoomId = null, explicitOperatorId = null) => {
    if (!themeDataPayload || typeof themeDataPayload !== 'object') {
        return {
            success: false,
            message: 'El cuerpo de datos del tema es obligatorio.'
        };
    }

    const trimmedName = typeof themeDataPayload.name === 'string' ? themeDataPayload.name.trim() : '';
    if (!trimmedName) {
        return {
            success: false,
            message: 'El nombre del tema es obligatorio y no puede estar vacío.'
        };
    }

    const resolvedRoomId = themeDataPayload.roomId || explicitRoomId || null;
    const resolvedOperatorId = themeDataPayload.operatorId || explicitOperatorId || null;

    const sanitizedPayload = {
        name: trimmedName,
        roomId: resolvedRoomId,
        operatorId: resolvedOperatorId,
        isSystemPreset: false,
        brandLogoUrl: themeDataPayload.brandLogoUrl || null,
        sponsorLogoUrl: themeDataPayload.sponsorLogoUrl || null,
        coverTitle: themeDataPayload.coverTitle || '¡ESTIRA Y AFLOJA!',
        coverBackgroundType: themeDataPayload.coverBackgroundType || 'color',
        coverBackgroundColor: themeDataPayload.coverBackgroundColor || '#0f172a',
        coverBackgroundUrl: themeDataPayload.coverBackgroundUrl || null,
        coverTextColor: themeDataPayload.coverTextColor || '#ffffff',
        gameBackgroundType: themeDataPayload.gameBackgroundType || 'color',
        gameBackgroundColor: themeDataPayload.gameBackgroundColor || '#020617',
        gameBackgroundUrl: themeDataPayload.gameBackgroundUrl || null,
        gameTextColor: themeDataPayload.gameTextColor || '#ffffff',
        fanBackgroundType: themeDataPayload.fanBackgroundType || 'color',
        fanBackgroundColor: themeDataPayload.fanBackgroundColor || '#0f172a',
        fanBackgroundUrl: themeDataPayload.fanBackgroundUrl || null,
        fanTextColor: themeDataPayload.fanTextColor || '#ffffff',
        ropeTextureUrl: themeDataPayload.ropeTextureUrl || null,
        mudPuddleColor: themeDataPayload.mudPuddleColor || '#451a03',
        mudPuddleUrl: themeDataPayload.mudPuddleUrl || null,
        player1Name: themeDataPayload.player1Name || 'EQUIPO 1',
        player1Color: themeDataPayload.player1Color || '#0284c7',
        player1Phase1Avatar: themeDataPayload.player1Phase1Avatar || null,
        player1Phase2Avatar: themeDataPayload.player1Phase2Avatar || null,
        player1Phase3Avatar: themeDataPayload.player1Phase3Avatar || null,
        player1LoserAvatar: themeDataPayload.player1LoserAvatar || themeDataPayload.player1Loser || null,
        player1WinnerAvatar: themeDataPayload.player1WinnerAvatar || themeDataPayload.player1Winner || null,
        player2Name: themeDataPayload.player2Name || 'EQUIPO 2',
        player2Color: themeDataPayload.player2Color || '#ef4444',
        player2Phase1Avatar: themeDataPayload.player2Phase1Avatar || null,
        player2Phase2Avatar: themeDataPayload.player2Phase2Avatar || null,
        player2Phase3Avatar: themeDataPayload.player2Phase3Avatar || null,
        player2LoserAvatar: themeDataPayload.player2LoserAvatar || themeDataPayload.player2Loser || null,
        player2WinnerAvatar: themeDataPayload.player2WinnerAvatar || themeDataPayload.player2Winner || null,
        fontFamily: themeDataPayload.fontFamily || 'Bangers'
    };

    return await CreateThemeDL(sanitizedPayload);
};

/**
 * Actualiza un tema existente de Estira y Afloja validando identificador y nombres obligatorios.
 * @param {number|string} themeIdentifier - Identificador numérico del tema a actualizar.
 * @param {Object} themeDataPayload - Datos actualizados del tema visual.
 * @param {string|null} [explicitRoomId=null] - Identificador GUID opcional de la sala.
 * @param {string|null} [explicitOperatorId=null] - Identificador GUID opcional del operador.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado de la actualización.
 */
export const UpdateThemeBL = async (themeIdentifier, themeDataPayload, explicitRoomId = null, explicitOperatorId = null) => {
    if (!themeIdentifier && themeIdentifier !== 0) {
        return {
            success: false,
            message: 'El identificador del tema es obligatorio.'
        };
    }

    if (!themeDataPayload || typeof themeDataPayload !== 'object') {
        return {
            success: false,
            message: 'El cuerpo de datos para actualizar el tema es obligatorio.'
        };
    }

    const trimmedName = typeof themeDataPayload.name === 'string' ? themeDataPayload.name.trim() : '';
    if (!trimmedName) {
        return {
            success: false,
            message: 'El nombre del tema es obligatorio y no puede estar vacío.'
        };
    }

    const resolvedRoomId = themeDataPayload.roomId || explicitRoomId || null;
    const resolvedOperatorId = themeDataPayload.operatorId || explicitOperatorId || null;

    const sanitizedPayload = {
        name: trimmedName,
        roomId: resolvedRoomId,
        operatorId: resolvedOperatorId,
        brandLogoUrl: themeDataPayload.brandLogoUrl || null,
        sponsorLogoUrl: themeDataPayload.sponsorLogoUrl || null,
        coverTitle: themeDataPayload.coverTitle || '¡ESTIRA Y AFLOJA!',
        coverBackgroundType: themeDataPayload.coverBackgroundType || 'color',
        coverBackgroundColor: themeDataPayload.coverBackgroundColor || '#0f172a',
        coverBackgroundUrl: themeDataPayload.coverBackgroundUrl || null,
        coverTextColor: themeDataPayload.coverTextColor || '#ffffff',
        gameBackgroundType: themeDataPayload.gameBackgroundType || 'color',
        gameBackgroundColor: themeDataPayload.gameBackgroundColor || '#020617',
        gameBackgroundUrl: themeDataPayload.gameBackgroundUrl || null,
        gameTextColor: themeDataPayload.gameTextColor || '#ffffff',
        fanBackgroundType: themeDataPayload.fanBackgroundType || 'color',
        fanBackgroundColor: themeDataPayload.fanBackgroundColor || '#0f172a',
        fanBackgroundUrl: themeDataPayload.fanBackgroundUrl || null,
        fanTextColor: themeDataPayload.fanTextColor || '#ffffff',
        ropeTextureUrl: themeDataPayload.ropeTextureUrl || null,
        mudPuddleColor: themeDataPayload.mudPuddleColor || '#451a03',
        mudPuddleUrl: themeDataPayload.mudPuddleUrl || null,
        player1Name: themeDataPayload.player1Name || 'EQUIPO 1',
        player1Color: themeDataPayload.player1Color || '#0284c7',
        player1Phase1Avatar: themeDataPayload.player1Phase1Avatar || null,
        player1Phase2Avatar: themeDataPayload.player1Phase2Avatar || null,
        player1Phase3Avatar: themeDataPayload.player1Phase3Avatar || null,
        player1LoserAvatar: themeDataPayload.player1LoserAvatar || themeDataPayload.player1Loser || null,
        player1WinnerAvatar: themeDataPayload.player1WinnerAvatar || themeDataPayload.player1Winner || null,
        player2Name: themeDataPayload.player2Name || 'EQUIPO 2',
        player2Color: themeDataPayload.player2Color || '#ef4444',
        player2Phase1Avatar: themeDataPayload.player2Phase1Avatar || null,
        player2Phase2Avatar: themeDataPayload.player2Phase2Avatar || null,
        player2Phase3Avatar: themeDataPayload.player2Phase3Avatar || null,
        player2LoserAvatar: themeDataPayload.player2LoserAvatar || themeDataPayload.player2Loser || null,
        player2WinnerAvatar: themeDataPayload.player2WinnerAvatar || themeDataPayload.player2Winner || null,
        fontFamily: themeDataPayload.fontFamily || 'Bangers'
    };

    return await UpdateThemeDL(themeIdentifier, sanitizedPayload);
};

/**
 * Elimina un tema personalizado de Estira y Afloja.
 * @param {number|string} themeIdentifier - Identificador numérico del tema a eliminar.
 * @returns {Promise<{success: boolean, message?: string}>} Confirmación de eliminación.
 */
export const DeleteThemeBL = async (themeIdentifier) => {
    if (!themeIdentifier && themeIdentifier !== 0) {
        return {
            success: false,
            message: 'El identificador del tema a eliminar es obligatorio.'
        };
    }

    return await DeleteThemeDL(themeIdentifier);
};

/**
 * Clona un tema existente generando una nueva copia editable e independiente.
 * @param {number|string} themeIdentifier - Identificador del tema original.
 * @param {string|null} [newThemeName=null] - Nombre para la nueva copia (opcional).
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Tema clonado.
 */
export const CloneThemeBL = async (themeIdentifier, newThemeName = null) => {
    if (!themeIdentifier && themeIdentifier !== 0) {
        return {
            success: false,
            message: 'El identificador del tema a clonar es obligatorio.'
        };
    }

    const sanitizedName = typeof newThemeName === 'string' && newThemeName.trim() ? newThemeName.trim() : null;
    return await CloneThemeDL(themeIdentifier, sanitizedName);
};

// =========================================================================
// CONFIGURACIÓN E HISTORIAL DE SALA
// =========================================================================

/**
 * Obtiene la configuración activa de Estira y Afloja para una sala validando el identificador.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Configuración activa.
 */
export const GetConfigBL = async (roomId) => {
    const effectiveRoomId = roomId || (typeof localStorage !== 'undefined'
        ? (localStorage.getItem('active_room_id') || localStorage.getItem('roomId') || localStorage.getItem('selected_room_id') || localStorage.getItem('RoomId'))
        : '') || '';

    if (!effectiveRoomId) {
        return {
            success: false,
            message: 'El identificador de la sala (RoomId) es obligatorio.'
        };
    }

    return await GetConfigDL(String(effectiveRoomId).trim());
};

/**
 * Guarda o actualiza la configuración de Estira y Afloja para una sala con valores normalizados.
 * @param {Object|string} param1 - Datos de configuración o identificador de sala.
 * @param {string|Object} [param2] - Identificador de sala o datos de configuración.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Configuración guardada.
 */
export const SaveConfigBL = async (param1, param2) => {
    let payload = {};
    let roomId = '';

    if (param1 && typeof param1 === 'object') {
        payload = { ...param1 };
        roomId = param2 || payload.roomId || '';
    } else if (typeof param1 === 'string' && param2 && typeof param2 === 'object') {
        payload = { ...param2 };
        roomId = param1;
    } else if (typeof param1 === 'string') {
        roomId = param1;
    }

    if (!roomId) {
        roomId = (typeof localStorage !== 'undefined'
            ? (localStorage.getItem('active_room_id') || localStorage.getItem('roomId') || localStorage.getItem('selected_room_id') || localStorage.getItem('RoomId'))
            : '') || '';
    }

    if (!roomId) {
        return {
            success: false,
            message: 'El identificador de sala (RoomId) es obligatorio para guardar la configuración.'
        };
    }

    const sanitizedConfig = {
        roomId: String(roomId).trim(),
        activeThemeId: payload.activeThemeId !== undefined && payload.activeThemeId !== null
            ? (Number(payload.activeThemeId) > 0 ? Number(payload.activeThemeId) : null)
            : null,
        gameMode: payload.gameMode || 'Timed',
        displacementStyle: payload.displacementStyle || 'DynamicBias',
        roundDurationSeconds: Math.max(5, Math.min(120, Number(payload.roundDurationSeconds || payload.roundDuration || payload.durationSeconds) || 15)),
        enableOvertimeOnDraw: Boolean(payload.enableOvertimeOnDraw)
    };

    return await SaveConfigDL(sanitizedConfig);
};

/**
 * Obtiene el historial de partidas disputadas en una sala garantizando una lista estructurada.
 * @param {string|number} param1 - Identificador GUID de la sala o límite numérico.
 * @param {number|string} [param2] - Límite numérico o identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data: Array<Object>, message?: string}>} Lista de partidas.
 */
export const GetMatchHistoryBL = async (param1, param2) => {
    let effectiveRoomId = '';
    let effectiveLimit = 20;

    if (typeof param1 === 'number' || (!isNaN(Number(param1)) && typeof param2 === 'string' && param2.length > 10)) {
        effectiveLimit = Number(param1) || 20;
        effectiveRoomId = param2 || '';
    } else {
        effectiveRoomId = param1 || '';
        effectiveLimit = Number(param2) || 20;
    }

    if (!effectiveRoomId || !String(effectiveRoomId).trim() || !isNaN(Number(effectiveRoomId))) {
        return {
            success: false,
            data: [],
            message: 'El identificador de la sala es obligatorio para consultar el historial.'
        };
    }

    const sanitizedLimit = Math.max(1, Math.min(100, Number(effectiveLimit) || 20));
    const dlResponse = await GetMatchHistoryDL(String(effectiveRoomId).trim(), sanitizedLimit);

    if (!dlResponse.success) {
        return {
            success: false,
            data: [],
            message: dlResponse.message || 'No fue posible consultar el historial de partidas.'
        };
    }

    return {
        success: true,
        data: Array.isArray(dlResponse.data) ? dlResponse.data : []
    };
};

/**
 * Registra y persiste las estadísticas finales de una partida disputada de Estira y Afloja.
 * @param {Object} matchPayload - Métricas finales de la partida.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado persistido.
 */
export const SaveMatchResultBL = async (matchPayload) => {
    if (!matchPayload || !matchPayload.roomId) {
        return {
            success: false,
            message: 'El identificador de la sala es obligatorio para guardar el resultado de la partida.'
        };
    }

    const sanitizedMatch = {
        matchId: matchPayload.matchId || undefined,
        roomId: String(matchPayload.roomId).trim(),
        operatorId: matchPayload.operatorId || null,
        playedAt: matchPayload.playedAt || new Date().toISOString(),
        themeId: matchPayload.themeId ? Number(matchPayload.themeId) : null,
        themeName: matchPayload.themeName || null,
        gameMode: matchPayload.gameMode || 'Timed',
        displacementStyle: matchPayload.displacementStyle || 'DynamicBias',
        winner: matchPayload.winner || 'Empate',
        scoreJ1: Number(matchPayload.scoreJ1) || 0,
        scoreJ2: Number(matchPayload.scoreJ2) || 0,
        totalTapsJ1: Math.max(0, Number(matchPayload.totalTapsJ1) || 0),
        totalTapsJ2: Math.max(0, Number(matchPayload.totalTapsJ2) || 0),
        activeFansJ1: Math.max(0, Number(matchPayload.activeFansJ1) || 0),
        activeFansJ2: Math.max(0, Number(matchPayload.activeFansJ2) || 0),
        effectiveDurationSeconds: Math.max(1, Number(matchPayload.effectiveDurationSeconds) || 15),
        wasSuddenDeathTriggered: Boolean(matchPayload.wasSuddenDeathTriggered),
        wasEmergencyStopped: Boolean(matchPayload.wasEmergencyStopped)
    };

    return await SaveMatchResultDL(sanitizedMatch);
};

// =========================================================================
// CARGA MULTIMEDIA
// =========================================================================

/**
 * Valida y sube un archivo gráfico para TugOfWar.
 * @param {File|Blob|FormData} fileOrFormData - Archivo a transmitir.
 * @returns {Promise<{success: boolean, data?: Object, url?: string, message?: string}>} URL del archivo.
 */
export const UploadAssetBL = async (fileOrFormData) => {
    if (!fileOrFormData) {
        return {
            success: false,
            message: 'No se ha seleccionado ningún archivo para cargar.'
        };
    }

    return await UploadAssetDL(fileOrFormData);
};

/**
 * Valida y sube un lote masivo de hasta 50 archivos multimedia para avatares o fondos de TugOfWar.
 * @param {Array<File>|FileList|FormData} filesOrFormData - Colección de archivos a transmitir.
 * @returns {Promise<{success: boolean, data?: Object, urls: Array<string>, files: Array<Object>, count: number, message?: string}>} Reporte de procesamiento.
 */
export const UploadBatchMediaBL = async (filesOrFormData) => {
    if (!filesOrFormData) {
        return {
            success: false,
            urls: [],
            files: [],
            count: 0,
            message: 'No se seleccionaron archivos para la carga masiva.'
        };
    }

    if (!(filesOrFormData instanceof FormData)) {
        const filesArray = Array.from(filesOrFormData || []);
        if (filesArray.length === 0) {
            return {
                success: false,
                urls: [],
                files: [],
                count: 0,
                message: 'La lista de archivos a cargar está vacía.'
            };
        }

        if (filesArray.length > 50) {
            return {
                success: false,
                urls: [],
                files: [],
                count: 0,
                message: 'El límite máximo para subida por lote es de 50 archivos.'
            };
        }
    }

    return await UploadBatchMediaDL(filesOrFormData);
};

// =========================================================================
// COMANDOS DE CONTROL DE SALA E INTEGRACIÓN CON SCREEN V2
// =========================================================================

/**
 * Proyecta la portada de Estira y Afloja en la pantalla gigante y notifica al lienzo ScreenV2 ('TugOfWarCover').
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {number|string|null} [themeIdentifier=null] - Identificador del tema visual.
 * @param {number} [screenIdentifier=1] - Identificador de la pantalla en ScreenV2.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado de la portada proyectada.
 */
export const ShowCoverBL = async (roomId, themeIdentifier = null, screenIdentifier = 1) => {
    if (!roomId) {
        return {
            success: false,
            message: 'El identificador de sala es obligatorio para proyectar la portada.'
        };
    }

    try {
        const dlResponse = await ShowCoverDL(roomId, themeIdentifier || 0);

        try {
            await SetScreenV2MainDynamic(screenIdentifier, 'TugOfWarCover');
        } catch (screenError) {
            // Silencioso si ScreenV2 no está activo en este entorno
        }

        return dlResponse.success
            ? { success: true, data: dlResponse.data, message: 'Portada de Estira y Afloja proyectada exitosamente.' }
            : dlResponse;
    } catch (unexpectedError) {
        return {
            success: false,
            message: 'Error al proyectar la portada de Estira y Afloja.'
        };
    }
};

/**
 * Proyecta el escenario de juego de Estira y Afloja y notifica al lienzo ScreenV2 ('TugOfWar').
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {number|string|null} [themeIdentifier=null] - Identificador del tema visual.
 * @param {string} [gameMode='Timed'] - Modo de juego ('Timed' | 'SuddenDeath').
 * @param {string} [displacementStyle='DynamicBias'] - Estilo de física de soga ('DynamicBias' | 'VisualSuspense').
 * @param {number} [durationSeconds=15] - Duración de la ronda en segundos.
 * @param {boolean} [enableOvertimeOnDraw=false] - Habilitar tiempo extra en empate.
 * @param {number} [screenIdentifier=1] - Identificador de la pantalla en ScreenV2.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado del escenario de juego preparado.
 */
export const ShowGameSceneBL = async (
    roomId,
    themeIdentifier = null,
    gameMode = 'Timed',
    displacementStyle = 'DynamicBias',
    durationSeconds = 15,
    enableOvertimeOnDraw = false,
    screenIdentifier = 1
) => {
    if (!roomId) {
        return {
            success: false,
            message: 'El identificador de sala es obligatorio para proyectar el juego.'
        };
    }

    try {
        const sanitizedDuration = Math.max(5, Math.min(120, Number(durationSeconds) || 15));
        const dlResponse = await ShowGameSceneDL(
            roomId,
            themeIdentifier || 0,
            gameMode,
            displacementStyle,
            sanitizedDuration,
            enableOvertimeOnDraw
        );

        try {
            await SetScreenV2MainDynamic(screenIdentifier, 'TugOfWar');
        } catch (screenError) {
            // Silencioso si ScreenV2 no está activo en este entorno
        }

        return dlResponse.success
            ? { success: true, data: dlResponse.data, message: 'Escenario de juego proyectado exitosamente.' }
            : dlResponse;
    } catch (unexpectedError) {
        return {
            success: false,
            message: 'Error al proyectar el escenario de juego de TugOfWar.'
        };
    }
};

/**
 * Inicia la cuenta regresiva sincronizada (3, 2, 1) y arranque de la partida.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado de la partida iniciada.
 */
export const StartCountdownBL = async (roomId) => {
    if (!roomId) {
        return {
            success: false,
            message: 'El identificador de la sala es obligatorio para iniciar la partida.'
        };
    }

    return await StartCountdownDL(roomId);
};

/**
 * Pausa temporalmente la partida activa en la sala.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Confirmación de pausa.
 */
export const PauseMatchBL = async (roomId) => {
    if (!roomId) {
        return {
            success: false,
            message: 'El identificador de sala es obligatorio para pausar la partida.'
        };
    }

    return await PauseMatchDL(roomId);
};

/**
 * Reanuda la partida activa en la sala.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Confirmación de reanudación.
 */
export const ResumeMatchBL = async (roomId) => {
    if (!roomId) {
        return {
            success: false,
            message: 'El identificador de sala es obligatorio para reanudar la partida.'
        };
    }

    return await ResumeMatchDL(roomId);
};

/**
 * Finaliza anticipadamente la partida activa, calcula el ganador y persiste en base de datos.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado final calculado.
 */
export const FinishEarlyBL = async (roomId) => {
    if (!roomId) {
        return {
            success: false,
            message: 'El identificador de sala es obligatorio para finalizar la partida.'
        };
    }

    return await FinishEarlyDL(roomId);
};

/**
 * Reinicia la partida de la sala devolviéndola al estado inactivo.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado reiniciado.
 */
export const ResetMatchBL = async (roomId) => {
    if (!roomId) {
        return {
            success: false,
            message: 'El identificador de sala es obligatorio para reiniciar la partida.'
        };
    }

    return await ResetMatchDL(roomId);
};

/**
 * Obtiene el estado actual en memoria de la partida de TugOfWar.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado en vivo.
 */
export const GetMatchStateBL = async (roomId) => {
    if (!roomId) {
        return {
            success: false,
            message: 'El identificador de sala es obligatorio para consultar el estado de la partida.'
        };
    }

    return await GetMatchStateDL(roomId);
};

// =========================================================================
// TELEMETRÍA Y AFICIONADOS (TAPS Y BANDO)
// =========================================================================

/**
 * Procesa y envía un lote de toques (taps) a la sala aplicando sanitización y resolución de bando polimórfica.
 * @param {string|Object} param1 - Identificador de sala o payload estructurado.
 * @param {string|number} [param2] - Bando ('player1'|'player2'|'J1'|'J2'), connectionId o tapCount.
 * @param {number|string} [param3] - tapCount o connectionId.
 * @param {string} [param4] - Identificador SignalR de la conexión activa.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado del procesamiento.
 */
export const SendTapBatchBL = async (param1, param2, param3, param4) => {
    let effectiveRoomId = '';
    let effectiveChoice = 'player1';
    let effectiveTaps = 1;
    let effectiveConnectionId = '';

    const args = [param1, param2, param3, param4].filter((x) => x !== undefined && x !== null);

    if (param1 && typeof param1 === 'object') {
        effectiveRoomId = param1.roomId || param2 || '';
        effectiveChoice = param1.playerChoice || param1.side || 'player1';
        effectiveTaps = Number(param1.tapCount || param1.taps) || 1;
        effectiveConnectionId = param1.connectionId || '';
    } else {
        effectiveRoomId = String(param1 || '').trim();

        for (let i = 1; i < args.length; i++) {
            const val = args[i];
            if (typeof val === 'number' || (!isNaN(Number(val)) && typeof val !== 'string')) {
                effectiveTaps = Number(val);
            } else if (typeof val === 'string') {
                const lower = val.toLowerCase().trim();
                if (lower === 'player1' || lower === 'j1' || lower === '1') {
                    effectiveChoice = 'player1';
                } else if (lower === 'player2' || lower === 'j2' || lower === '2') {
                    effectiveChoice = 'player2';
                } else if (lower.length > 5 && !effectiveConnectionId) {
                    effectiveConnectionId = val;
                }
            }
        }
    }

    const normLower = String(effectiveChoice).toLowerCase().trim();
    if (normLower === 'j2' || normLower === 'player2' || normLower === '2') {
        effectiveChoice = 'player2';
    } else {
        effectiveChoice = 'player1';
    }

    if (!effectiveRoomId) {
        effectiveRoomId = (typeof localStorage !== 'undefined'
            ? (localStorage.getItem('active_room_id') || localStorage.getItem('roomId') || localStorage.getItem('selected_room_id') || localStorage.getItem('RoomId'))
            : '') || '';
    }

    if (!effectiveRoomId) {
        return {
            success: false,
            message: 'El identificador de sala es obligatorio para registrar toques.'
        };
    }

    const sanitizedTaps = Math.max(1, Math.min(100, Number(effectiveTaps) || 1));
    return await SendTapBatchDL(effectiveRoomId, effectiveChoice, sanitizedTaps, effectiveConnectionId);
};

/**
 * Registra formalmente al aficionado en un bando ("player1" o "player2") para el cálculo del promedio per cápita.
 * @param {string|Object} param1 - Identificador GUID de la sala o payload.
 * @param {string} [param2] - Bando seleccionado ('player1' | 'player2' | 'J1' | 'J2').
 * @param {string} [param3] - Identificador SignalR de la conexión activa.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado actualizado.
 */
export const RegisterFanBL = async (param1, param2, param3) => {
    let effectiveRoomId = '';
    let effectiveChoice = 'player1';
    let effectiveConnectionId = '';

    const args = [param1, param2, param3].filter((x) => x !== undefined && x !== null);

    if (param1 && typeof param1 === 'object') {
        effectiveRoomId = param1.roomId || param2 || '';
        effectiveChoice = param1.playerChoice || param1.side || 'player1';
        effectiveConnectionId = param1.connectionId || '';
    } else {
        effectiveRoomId = String(param1 || '').trim();

        for (let i = 1; i < args.length; i++) {
            const val = args[i];
            if (typeof val === 'string') {
                const lower = val.toLowerCase().trim();
                if (lower === 'player1' || lower === 'j1' || lower === '1') {
                    effectiveChoice = 'player1';
                } else if (lower === 'player2' || lower === 'j2' || lower === '2') {
                    effectiveChoice = 'player2';
                } else if (lower.length > 5 && !effectiveConnectionId) {
                    effectiveConnectionId = val;
                }
            }
        }
    }

    const normLower = String(effectiveChoice).toLowerCase().trim();
    if (normLower === 'j2' || normLower === 'player2' || normLower === '2') {
        effectiveChoice = 'player2';
    } else {
        effectiveChoice = 'player1';
    }

    if (!effectiveRoomId) {
        effectiveRoomId = (typeof localStorage !== 'undefined'
            ? (localStorage.getItem('active_room_id') || localStorage.getItem('roomId') || localStorage.getItem('selected_room_id') || localStorage.getItem('RoomId'))
            : '') || '';
    }

    if (!effectiveRoomId) {
        return {
            success: false,
            message: 'El identificador de sala es obligatorio para registrar al aficionado.'
        };
    }

    return await RegisterFanDL(effectiveRoomId, effectiveChoice, effectiveConnectionId);
};

/**
 * Desregistra formalmente al aficionado del bando activo al salir o desconectarse.
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {string} [connectionId=''] - Identificador SignalR de la conexión.
 * @param {string} [playerChoice='player1'] - Bando del que se desregistra.
 * @returns {Promise<{success: boolean, message?: string}>} Confirmación de desregistro.
 */
export const UnregisterFanBL = async (roomId, connectionId = '', playerChoice = 'player1') => {
    const effectiveRoomId = roomId || (typeof localStorage !== 'undefined'
        ? (localStorage.getItem('active_room_id') || localStorage.getItem('roomId') || localStorage.getItem('selected_room_id') || localStorage.getItem('RoomId'))
        : '') || '';

    if (!effectiveRoomId) {
        return {
            success: false,
            message: 'El identificador de sala es obligatorio para desregistrar al aficionado.'
        };
    }

    return await UnregisterFanDL(effectiveRoomId, connectionId, playerChoice);
};

// =========================================================================
// UTILIDADES MATEMÁTICAS Y DE FÍSICA DE JUEGO
// =========================================================================

/**
 * Calcula el promedio ponderado per cápita de toques por aficionado activo.
 * Fórmula autoritativa: Math.max(0, totalTaps) / Math.max(1, activeFans)
 * @param {number} totalTaps - Total de toques acumulados por el bando.
 * @param {number} activeFans - Cantidad de aficionados conectados activamente al bando.
 * @returns {number} Promedio ponderado redondeado a 2 decimales.
 */
export const CalculateWeightedAverage = (totalTaps, activeFans) => {
    const validTaps = Math.max(0, Number(totalTaps) || 0);
    const validFans = Math.max(1, Number(activeFans) || 0);
    const averageScore = validTaps / validFans;
    return Math.round(averageScore * 100) / 100;
};

/**
 * Calcula la proporción porcentual de fuerza entre ambos equipos normalizada a 100%.
 * @param {number} score1 - Puntaje o promedio del Equipo 1.
 * @param {number} score2 - Puntaje o promedio del Equipo 2.
 * @returns {{ percentage1: number, percentage2: number }} Porcentajes normalizados (suman 100%).
 */
export const CalculatePullPercentage = (score1, score2) => {
    const s1 = Math.max(0, Number(score1) || 0);
    const s2 = Math.max(0, Number(score2) || 0);
    const total = s1 + s2;

    if (total === 0) {
        return { percentage1: 50.0, percentage2: 50.0 };
    }

    const p1 = Math.round((s1 / total) * 10000) / 100;
    const p2 = Math.round((100 - p1) * 100) / 100;
    return { percentage1: p1, percentage2: p2 };
};

/**
 * Determina el tercio o nivel de tensión (Tier 1, 2 o 3) según la posición relativa o porcentaje de tracción.
 * Tier 1: 100% a 67% (Energía alta / Fase Normal)
 * Tier 2: 66% a 34% (Esfuerzo medio / Fase Sudor)
 * Tier 3: 33% a 0% (Al borde del lodo / Fase Agotamiento)
 * 
 * @param {number} pullPercentage - Porcentaje de fuerza del equipo (0 a 100).
 * @returns {number} Nivel de fase (1, 2 o 3).
 */
export const DeterminePhaseTier = (pullPercentage) => {
    const pct = Math.max(0, Math.min(100, Number(pullPercentage) || 50));
    if (pct >= 67) return 1;
    if (pct >= 34) return 2;
    return 3;
};

/**
 * Calcula el desplazamiento horizontal de la soga en unidades viewport (vw) según los puntajes y el estilo de física.
 * @param {number} score1 - Puntaje del Equipo 1.
 * @param {number} score2 - Puntaje del Equipo 2.
 * @param {string} [displacementStyle='DynamicBias'] - Estilo de física ('DynamicBias' | 'VisualSuspense').
 * @param {number} [maxVw=30] - Límite máximo de desplazamiento en unidades vw (de -maxVw a +maxVw).
 * @returns {number} Desplazamiento en vw (negativo: Equipo 1 gana; positivo: Equipo 2 gana; 0: centro).
 */
export const CalculateRopeDisplacement = (score1, score2, displacementStyle = 'DynamicBias', maxVw = 30) => {
    const s1 = Math.max(0, Number(score1) || 0);
    const s2 = Math.max(0, Number(score2) || 0);
    const limitVw = Math.max(5, Math.min(50, Number(maxVw) || 30));

    if (s1 === 0 && s2 === 0) return 0.0;

    const diff = s2 - s1; // Positivo si Equipo 2 jala más, Negativo si Equipo 1 jala más
    const total = s1 + s2;

    if (displacementStyle === 'VisualSuspense') {
        // Modelo amortiguado / sigmoide con suspenso visual
        const normalizedRatio = diff / Math.max(1, total);
        const curvedRatio = Math.sign(normalizedRatio) * Math.pow(Math.abs(normalizedRatio), 1.4);
        const displacement = curvedRatio * limitVw;
        return Math.round(displacement * 100) / 100;
    }

    // DynamicBias: Proporción directa suave
    const normalizedBias = diff / Math.max(1, total);
    const displacement = normalizedBias * limitVw;
    return Math.round(displacement * 100) / 100;
};

// =========================================================================
// ALIASES DE CONVENIENCIA Y COMPATIBILIDAD
// =========================================================================
export const GetThemes = (roomId, opId) => GetThemesBL(roomId, opId);
export const GetTheme = (id, roomId) => GetThemeByIdBL(id, roomId);
export const CreateTheme = (data, roomId, opId) => CreateThemeBL(data, roomId, opId);
export const UpdateTheme = (id, data, roomId, opId) => UpdateThemeBL(id, data, roomId, opId);
export const SaveTheme = (data, roomId, opId) => {
    if (data && (Number(data.id) > 0 || Number(data.themeId) > 0)) {
        const id = data.id || data.themeId;
        return UpdateThemeBL(id, data, roomId, opId);
    }
    return CreateThemeBL(data, roomId, opId);
};
export const DeleteTheme = (id, roomId, opId) => DeleteThemeBL(id, roomId, opId);
export const CloneTheme = (id, roomId, opId) => CloneThemeBL(id, roomId, opId);
export const GetConfig = (roomId) => GetConfigBL(roomId);
export const SaveConfig = (data, roomId) => SaveConfigBL(data, roomId);
export const GetMatchesHistory = (param1, param2) => GetMatchHistoryBL(param1, param2);
export const UploadMedia = (file, category, roomId) => UploadAssetBL(file, category, roomId);
export const UploadBatchMedia = (files, category, roomId) => UploadBatchMediaBL(files, category, roomId);
export const ShowCover = (roomId, themeId) => ShowCoverBL(roomId, themeId);
export const ShowGame = (roomId, themeId, mode, style, dur, overtime) => ShowGameSceneBL(roomId, themeId, mode, style, dur, overtime);
export const StartGame = (roomId) => StartCountdownBL(roomId);
export const PauseGame = (roomId) => PauseMatchBL(roomId);
export const ResumeGame = (roomId) => ResumeMatchBL(roomId);
export const FinishEarly = (roomId) => FinishEarlyBL(roomId);
export const ResetGame = (roomId) => ResetMatchBL(roomId);
export const GetRealtimeState = (roomId) => GetMatchStateBL(roomId);

export const SaveFanSessionData = (roomId, sessionData) => SaveFanSessionBL(roomId, sessionData);
export const GetFanSessionData = (roomId) => GetFanSessionBL(roomId);
export const ClearFanSessionData = (roomId) => ClearFanSessionBL(roomId);

export default {
    ResolveMediaUrl,
    PreloadImages,
    PreloadGameAssets,
    SaveFanSessionBL,
    GetFanSessionBL,
    ClearFanSessionBL,
    GetThemesBL,
    GetThemeByIdBL,
    CreateThemeBL,
    UpdateThemeBL,
    DeleteThemeBL,
    CloneThemeBL,
    GetConfigBL,
    SaveConfigBL,
    GetMatchHistoryBL,
    SaveMatchResultBL,
    UploadAssetBL,
    UploadBatchMediaBL,
    ShowCoverBL,
    ShowGameSceneBL,
    StartCountdownBL,
    PauseMatchBL,
    ResumeMatchBL,
    FinishEarlyBL,
    ResetMatchBL,
    GetMatchStateBL,
    SendTapBatchBL,
    RegisterFanBL,
    UnregisterFanBL,
    CalculateWeightedAverage,
    CalculatePullPercentage,
    DeterminePhaseTier,
    CalculateRopeDisplacement,
    GetThemes,
    GetTheme,
    CreateTheme,
    UpdateTheme,
    SaveTheme,
    DeleteTheme,
    CloneTheme,
    GetConfig,
    SaveConfig,
    GetMatchesHistory,
    UploadMedia,
    UploadBatchMedia,
    ShowCover,
    ShowGame,
    StartGame,
    PauseGame,
    ResumeGame,
    FinishEarly,
    ResetGame,
    GetRealtimeState,
    SaveFanSessionData,
    GetFanSessionData,
    ClearFanSessionData,
    getResolvedMediaUrl
};
